import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { chat as chatApi, slash as slashApi } from "../../lib/api";
import type {
  ChatMessage,
  ChatMessageRow,
  TaskProposal,
  TrackProposal,
} from "../../lib/types";

interface UseChatThreadParams {
  threadId: string | null;
  projectId: string | null;
  model?: string;
  /** Called whenever the thread list should refetch (after send / delete / new). */
  onThreadsChanged?: () => void;
  /** Called once a fresh thread gets its first server-assigned id. */
  onThreadCreated?: (id: string) => void;
}

/**
 * Shared chat data layer. Exposes the loaded message list (with optimistic
 * appends), a send function that routes ``#``/``/``/regular text correctly,
 * and a pending flag for the typing indicator. The right-pane chat is its
 * only consumer today; keep the data layer here so the chrome stays thin.
 */
export function useChatThread({
  threadId,
  projectId,
  model,
  onThreadsChanged,
  onThreadCreated,
}: UseChatThreadParams) {
  const qc = useQueryClient();
  // Optimistic + post-response messages that haven't yet flushed back through
  // a refetch. Cleared whenever the active thread switches.
  const [pendingMessages, setPendingMessages] = useState<ChatMessage[]>([]);
  // Text accumulating during a streaming turn. Non-null while the stream is
  // open so the UI can show a growing bubble instead of a spinner.
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  // Stable ref so async stream callbacks don't close over a stale threadId.
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;

  useEffect(() => {
    setPendingMessages([]);
    setStreamingText(null);
  }, [threadId]);

  const { data: thread } = useQuery({
    queryKey: ["chat-thread", threadId],
    queryFn: () => (threadId ? chatApi.getThread(threadId) : null),
    enabled: Boolean(threadId),
  });

  const serverMessages: ChatMessage[] = useMemo(() => {
    if (!thread) return [];
    return thread.messages.map(rowToMessage);
  }, [thread]);

  const messages: ChatMessage[] = useMemo(() => {
    if (pendingMessages.length === 0) return serverMessages;
    return [...serverMessages, ...pendingMessages];
  }, [serverMessages, pendingMessages]);

  const handleSend = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isPending) return;

      // `/cmd …` routes to the slash invoke endpoint; output is local-only
      // (slash invoke is its own service, not the chat persistence path).
      if (trimmed.startsWith("/")) {
        const [head, ...rest] = trimmed.split(/\s+/);
        const name = head.slice(1);
        const args = rest.join(" ");
        if (name === "clear") {
          setPendingMessages([]);
          return;
        }
        setPendingMessages((m) => [
          ...m,
          { role: "user", content: trimmed, timestamp: new Date().toISOString() },
        ]);
        try {
          const result = await slashApi.invoke({
            name,
            args,
            project_id: projectId ?? undefined,
          });
          if (result.side_effects.includes("clear-chat")) {
            setPendingMessages([]);
            return;
          }
          setPendingMessages((m) => [
            ...m,
            {
              role: "assistant",
              content: result.rendered || "_(no output)_",
              timestamp: new Date().toISOString(),
            },
          ]);
        } catch (e) {
          setPendingMessages((m) => [
            ...m,
            {
              role: "assistant",
              content: `_command failed: ${(e as Error).message}_`,
              timestamp: new Date().toISOString(),
            },
          ]);
        }
        return;
      }

      // Optimistic user bubble
      setPendingMessages((m) => [
        ...m,
        { role: "user", content: trimmed, timestamp: new Date().toISOString() },
      ]);
      setStreamingText("");
      setIsPending(true);
      const wasFresh = threadIdRef.current == null;

      try {
        const stream = chatApi.sendStream({
          message: trimmed,
          project_id: projectId ?? undefined,
          thread_id: threadIdRef.current ?? undefined,
          model: model || undefined,
        });

        let accumulated = "";
        for await (const event of stream) {
          if (event.type === "delta") {
            accumulated += event.text;
            setStreamingText(accumulated);
          } else if (event.type === "done") {
            // Replace streaming bubble with the final persisted message
            setStreamingText(null);
            setPendingMessages((m) => [
              ...m,
              {
                role: "assistant",
                content: event.content,
                timestamp: new Date().toISOString(),
                task_proposal: event.task_proposal ?? undefined,
                track_proposal: event.track_proposal ?? undefined,
              },
            ]);
            onThreadsChanged?.();
            if (wasFresh && event.thread_id) {
              onThreadCreated?.(event.thread_id);
            } else if (threadIdRef.current) {
              qc.invalidateQueries({
                queryKey: ["chat-thread", threadIdRef.current],
              });
            }
          }
        }
      } catch (e) {
        setStreamingText(null);
        setPendingMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: `_chat error: ${(e as Error).message}_`,
            timestamp: new Date().toISOString(),
          },
        ]);
      } finally {
        setIsPending(false);
      }
    },
    [projectId, model, isPending, onThreadsChanged, onThreadCreated, qc],
  );

  return {
    thread,
    messages,
    handleSend,
    pending: isPending,
    streamingText,
  };
}

function rowToMessage(row: ChatMessageRow): ChatMessage {
  return {
    role: row.role,
    content: row.content,
    timestamp: row.created_at,
    task_proposal:
      (row.task_proposal as TaskProposal | null) ?? undefined,
    track_proposal:
      (row.track_proposal as TrackProposal | null) ?? undefined,
  };
}
